/* eslint-disable @typescript-eslint/no-explicit-any -- any используется как значение параметров инвариантных типов */

import { Dictionary, MaybePromise, keys, objectMap } from '@direct-frontend/stdlib';

import { $internals as $formInternals, FormActionsMap, FormInstance } from '../form/types';
import { runFormValidators } from '../form/validation';
import { getFormStoreSingleton } from '../store/store';
import { BaseValidationError, ValidationCause, ValidationState, ValidatorType } from '../types';

import { ArrayField, BaseArrayField } from './createArrayField';
import { BaseObjectField, ObjectField } from './createObjectField';
import { BasePrimitiveField, PrimitiveField } from './createPrimitiveField';
import { $internals as $fieldApiInternals, FieldApi, createFieldApi } from './fieldApi';
import { BaseField, BaseFields, InferFieldValue } from './types';
import { cleanupValidationErrorsBySource } from './utils/cleanupValidationErrorsBySource';
import { getActualUnionFieldConstituent } from './utils/getActualUnionFieldConstituent';
import { stringifyFieldIdPath } from './utils/stringifyFieldIdPath';
import { parseFieldKey } from './utils/parseFieldKey';
import { getDefaultFieldState } from '../store/utils/getDefaultFieldState';
import { UnionField } from './createUnionField';
import { FieldValidationPromise } from '../store/types';

/**
 * Тип, описывающий валидатор поля
 * @typeParameter Field Тип поля
 **/
export type FieldValidator<Field extends BaseField> = (
    value: InferFieldValue<Field>,
    fieldApi: FieldApi<Field>,
    signal: AbortSignal
) => MaybePromise<void>;

/** Выводит тип ошибки валидации поля */
export type InferFieldValidationError<Field extends BaseField> =
    Field extends PrimitiveField<infer _, infer FieldValidationError, infer __, infer ___, infer ____, infer _____>
        ? FieldValidationError
        : Field extends ObjectField<infer _, infer FieldValidationError, infer __, infer ___, infer ____, infer _____>
          ? FieldValidationError
          : Field extends ArrayField<infer _, infer FieldValidationError, infer __, infer ___, infer ____, infer _____>
            ? FieldValidationError
            : Field extends UnionField<infer _, infer UnionConstituents, infer __>
              ? UnionConstituents[number] extends ObjectField<
                    infer _,
                    infer FieldValidationError,
                    infer __,
                    infer ___,
                    infer ____,
                    infer _____
                >
                  ? FieldValidationError
                  : never
              : never;

// Возвращает список необходимых валидаторов в зависимости от причины запуска
function getValidatorsTypesByValidationCause(
    validationCause: ValidationCause
): Array<'onChange' | 'onBlur' | 'onSubmit'> {
    switch (validationCause) {
        case 'change':
            return ['onChange'];
        case 'blur':
            return ['onBlur'];
        case 'submit':
            return ['onSubmit'];
        case 'revalidate':
            return ['onChange', 'onBlur'];
    }
}

// Возвращает список валидаторов заданного типа для поля
function getValidatorsByType<Field extends BasePrimitiveField | BaseObjectField | BaseArrayField>(
    field: Field,
    validatorType: ValidatorType
) {
    // blur валидаторы есть только у примитивных полей, в ином случае возвращаем пустой массив
    if (validatorType === 'onBlur') {
        return field.type === 'primitive' ? (field.validators?.onBlur ?? []) : [];
    }

    return field.validators?.[validatorType] ?? [];
}

// Параметры необходимые для запуска валидации
export type ValidationOptions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
> = {
    /** Причина запуска валидации */
    cause: ValidationCause;
    /** Инстанс формы, в которой запускается валидация */
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    /** Поле, для которого запускается валидация */
    field: BaseField;
    /** Id поля, для которого запускается валидация */
    fieldId: string;
    /** Флаг для отключения валидации внутренних полей, используется, например, при удалении элемента массива */
    notRevalidateInnerFields?: boolean;
};

// Запускает собственные валидаторы конкретного поля
function runSelfValidatorsForField<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validationOptions: ValidationOptions<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    validationState: ValidationState<FormValidationError>
) {
    const { getFieldValue, updateValidationMeta, getFieldsState } = getFormStoreSingleton(validationOptions.form.name);

    const fieldsState =
        getFieldsState({
            formInitializationId: validationOptions.form[$formInternals].formInitializationId,
        }) ?? {};
    const fieldId = validationOptions.fieldId;
    const fieldValidationMeta =
        validationState.fieldsValidation[fieldId] ??
        (validationState.fieldsValidation[fieldId] = {
            errorsMap: getDefaultFieldState().errorsMap,
            meta: getDefaultFieldState().validationMeta,
        });

    // Сбрасываем предыдущие ошибки валидации и отменяем запущенные асинхронные валидаторы
    switch (validationOptions.cause) {
        case 'change':
        case 'revalidate':
            cleanupValidationErrorsBySource(
                fieldsState,
                validationState.fieldsValidation,
                { type: 'field', fieldKey: fieldId },
                ['onChange', 'onBlur', 'onSubmit']
            );

            fieldValidationMeta?.meta.onChange.controller?.abort();
            fieldValidationMeta?.meta.onBlur.controller?.abort();
            fieldValidationMeta?.meta.onSubmit.controller?.abort();
            break;
        case 'blur':
            cleanupValidationErrorsBySource(
                fieldsState,
                validationState.fieldsValidation,
                { type: 'field', fieldKey: fieldId },
                ['onBlur', 'onSubmit']
            );

            fieldValidationMeta?.meta.onBlur.controller?.abort();
            fieldValidationMeta?.meta.onSubmit.controller?.abort();
            break;
        case 'submit':
            cleanupValidationErrorsBySource(
                fieldsState,
                validationState.fieldsValidation,
                { type: 'field', fieldKey: fieldId },
                ['onSubmit']
            );

            fieldValidationMeta?.meta.onSubmit.controller?.abort();
            break;
    }

    const fieldValue = getFieldValue({
        formInitializationId: validationOptions.form[$formInternals].formInitializationId,
        fieldId: validationOptions.fieldId,
    });
    const validatorsTypes = getValidatorsTypesByValidationCause(validationOptions.cause);

    for (const validatorsType of validatorsTypes) {
        // Если поле – юнион, то получать список валидаторов
        // и формировать fieldApi нужно из текущего объектного поля
        const field =
            validationOptions.field.type === 'union'
                ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- значение поля-юниона
                  getActualUnionFieldConstituent(validationOptions.field, fieldValue as Dictionary<unknown>)
                : validationOptions.field;

        const validatorsOfType = getValidatorsByType(field, validatorsType);

        if (!validatorsOfType.length) {
            continue;
        }

        // Перед запуском валидаторов создаем новый AbortController
        const controller = new AbortController();
        // И сбрасываем список промисов
        const promises: FieldValidationPromise[] = [];

        for (const validator of validatorsOfType) {
            const fieldApi = createFieldApi(validatorsType, { ...validationOptions, field }, validationState);
            const validatorResult = validator(
                fieldValue,
                // Метод `createFieldApi` возвращает Conditional Type и TS не может сопоставить его с типом аргумента
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                fieldApi as any,
                controller.signal
            );

            // Если валидатор асинхронный
            if (validatorResult instanceof Promise) {
                // То переключаем fieldApi в режим работы со слайсом, чтобы всегда работать с актуальным состоянием
                fieldApi[$fieldApiInternals].updateOptions({ storageType: 'slice' });

                // Так же запоминаем промис, чтобы родительские поля могли дождаться выполнения дочерних валидаторов
                promises.push(validatorResult);
            }
        }

        fieldValidationMeta.meta[validatorsType] = { controller, promises };

        // Если среди валидаторов есть хотя бы один асинхронный, то
        if (promises.length > 0) {
            // после выполнения всех асинхронных валидаторов нужно очистить метаданные валидации поля
            Promise.allSettled(promises).then(() => {
                // Если запуск валидаторов был отменен, то ничего очищать не нужно
                // В таком случае список промисов в слайсе уже отличается от текущего
                if (controller.signal.aborted) {
                    return;
                }

                updateValidationMeta({
                    formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                    fieldsValidationMeta: {
                        [fieldId]: {
                            [validatorsType]: {
                                controller: undefined,
                                promises: [],
                            },
                        },
                    },
                });
            });
        }
    }
}

// Запускает валидаторы всех дочерних полей заданного поля
function runAllInnerValidatorsForField<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validationOptions: ValidationOptions<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    validationState: ValidationState<FormValidationError>
) {
    const { getFieldState, getFieldValue } = getFormStoreSingleton(validationOptions.form.name);

    // Если поле – "объектное"
    if (validationOptions.field.type === 'object') {
        // То запускаем валидаторы всех полей этого объекта
        for (const innerFieldName of keys(validationOptions.field.fields)) {
            const field = validationOptions.field.fields[innerFieldName];
            const fieldId = stringifyFieldIdPath([validationOptions.fieldId, innerFieldName]);

            runAllInnerValidatorsForField({ ...validationOptions, field, fieldId }, validationState);
            runSelfValidatorsForField({ ...validationOptions, field, fieldId }, validationState);
        }
    }

    // Если поле – юнион
    if (validationOptions.field.type === 'union') {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- тип гарантируется публичным интерфейсом инстансов
        const value = getFieldValue({
            formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            fieldId: validationOptions.fieldId,
        }) as Dictionary<unknown>;

        const constituent = getActualUnionFieldConstituent(validationOptions.field, value);

        // То запускаем валидаторы того объектного поля, значение которого хранится в данный момент
        runAllInnerValidatorsForField({ ...validationOptions, field: constituent }, validationState);
    }

    // Если после – массив
    if (validationOptions.field.type === 'array') {
        const fieldState = getFieldState({
            formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            fieldId: validationOptions.fieldId,
        });

        if (fieldState && 'order' in fieldState) {
            // То запускаем валидаторы всех элементов массива
            for (const arrayItemId of fieldState.order) {
                const field: BaseField = validationOptions.field.field;

                runAllInnerValidatorsForField({ ...validationOptions, field, fieldId: arrayItemId }, validationState);

                if (field.type !== 'union') {
                    runSelfValidatorsForField({ ...validationOptions, field, fieldId: arrayItemId }, validationState);
                }
            }
        }
    }
}

// Запускает необходимые валидаторы и возвращает объект с ошибками для поля по заданному пути
function runValidatorsForPath<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validationOptions: ValidationOptions<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    validationState: ValidationState<FormValidationError>
) {
    function runValidators(
        currentLevelFields: BaseFields,
        fieldPathToValidate: string[],
        parentFieldsPath: string[] = []
    ) {
        const fieldName = fieldPathToValidate[0];
        if (!fieldName) {
            return;
        }

        const field = currentLevelFields[fieldName];
        if (!field) {
            return;
        }

        const currentFieldIdPath = [...parentFieldsPath, fieldName];
        const currentFieldId = stringifyFieldIdPath(currentFieldIdPath);

        // Если поле – "объектное"
        if (field.type === 'object') {
            // То сначала запускаем валидаторы для дочернего поля
            runValidators(field.fields, fieldPathToValidate.slice(1), currentFieldIdPath);
            // И только потом валидаторы самого объекта
            runSelfValidatorsForField({ ...validationOptions, field, fieldId: currentFieldId }, validationState);
        }

        // Если поле – юнион
        if (field.type === 'union') {
            const { getFieldValue } = getFormStoreSingleton(validationOptions.form.name);

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- тип гарантируется публичным интерфейсом инстансов
            const value = getFieldValue({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                fieldId: currentFieldId,
            }) as Dictionary<unknown>;

            const constituent = getActualUnionFieldConstituent(field, value);

            // То сначала запускаем валидаторы для дочернего поля
            runValidators(constituent.fields, fieldPathToValidate.slice(1), currentFieldIdPath);
            // И только потом валидаторы самого объекта
            runSelfValidatorsForField(
                { ...validationOptions, field: constituent, fieldId: currentFieldId },
                validationState
            );
        }

        // Если поле – массив
        if (field.type === 'array') {
            const itemId = fieldPathToValidate[1];
            // И путь еще не закончился
            if (itemId) {
                // То сначала запускаем валидаторы элемента этого массива
                runValidators({ [itemId]: field.field }, fieldPathToValidate.slice(1), currentFieldIdPath);
            }

            // И только потом валидаторы самого массива
            runSelfValidatorsForField({ ...validationOptions, field, fieldId: currentFieldId }, validationState);
        }

        // Если поле – "примитивное"
        if (field.type === 'primitive') {
            // То сразу запускаем валидаторы самого поля
            runSelfValidatorsForField({ ...validationOptions, field, fieldId: currentFieldId }, validationState);
        }
    }

    runValidators(validationOptions.form[$formInternals].fields, parseFieldKey(validationOptions.fieldId));

    // В конце запускаем валидаторы всей формы
    runFormValidators(validationOptions.cause, validationState, validationOptions.form);

    return validationState;
}

// Запускает валидаторы ВСЕХ полей формы (снизу-вверх)
function runAllValidatorsForForm<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validationOptions: Pick<
        ValidationOptions<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        'cause' | 'form'
    >,
    validationState: ValidationState<FormValidationError>
) {
    // Запускаем валидаторы для всех полей формы
    for (const fieldName of keys(validationOptions.form[$formInternals].fields)) {
        const field = validationOptions.form[$formInternals].fields[fieldName];
        const fieldId = fieldName;

        if (!field) {
            continue;
        }

        runAllInnerValidatorsForField({ ...validationOptions, field, fieldId }, validationState);
        runSelfValidatorsForField({ ...validationOptions, field, fieldId }, validationState);
    }

    // Затем запускаем валидаторы самой формы
    runFormValidators(validationOptions.cause, validationState, validationOptions.form);

    return validationState;
}

export function getValidationState<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
) {
    const { getFieldsState, getFormState, getFormValidationMeta } = getFormStoreSingleton(form.name);

    return {
        // Копируем объект с ошибками формы, чтобы иметь возможность мутировать его
        formValidationErrorsMap: objectMap(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Форма точно проинициализирована
            getFormState({ formInitializationId: form[$formInternals].formInitializationId })
                ?.errorsMap as ValidationState<FormValidationError>['formValidationErrorsMap'],
            validationErrors => [...validationErrors]
        ),

        // Копируем объект с метаданными валидации формы, чтобы иметь возможность мутировать его
        formValidationMeta: objectMap(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Форма точно проинициализирована
            getFormValidationMeta({ formInitializationId: form[$formInternals].formInitializationId })!,
            validationMeta => ({
                ...validationMeta,
            })
        ),

        // Копируем объект чтобы иметь возможность мутировать его
        fieldsValidation: objectMap(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Форма точно проинициализирована
            getFieldsState({ formInitializationId: form[$formInternals].formInitializationId })!,
            state => ({
                errorsMap: {
                    onChange: [...(state.errorsMap.onChange ?? [])],
                    onBlur: [...(state.errorsMap.onBlur ?? [])],
                    onSubmit: [...(state.errorsMap.onSubmit ?? [])],
                },
                meta: {
                    onChange: { ...state.validationMeta.onChange },
                    onBlur: { ...state.validationMeta.onBlur },
                    onSubmit: { ...state.validationMeta.onSubmit },
                },
            })
        ),
    };
}

// Запускает ВСЕ необходимые валидаторы (снизу-вверх) и сохраняет ошибки валидации поля по заданному пути:
// 1. Валидаторы всех дочерних полей
// 2. Валидаторы самого поля
// 3. Валидаторы всех родительских полей
export function validateField<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validationOptions: ValidationOptions<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
) {
    const { setValidationErrors } = getFormStoreSingleton(validationOptions.form.name);
    const validationState = getValidationState(validationOptions.form);

    if (!validationOptions.notRevalidateInnerFields) {
        runAllInnerValidatorsForField(validationOptions, validationState);
    }
    runValidatorsForPath(validationOptions, validationState);

    setValidationErrors({
        formInitializationId: validationOptions.form[$formInternals].formInitializationId,
        formValidationErrorsMap: validationState.formValidationErrorsMap,
        formValidationMeta: validationState.formValidationMeta,
        fieldsValidation: validationState.fieldsValidation,
    });
}

export function validateAll<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validationOptions: Pick<
        ValidationOptions<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        'cause' | 'form'
    >
) {
    const { setValidationErrors } = getFormStoreSingleton(validationOptions.form.name);
    const validationState = getValidationState(validationOptions.form);

    runAllValidatorsForForm(validationOptions, validationState);

    setValidationErrors({
        formInitializationId: validationOptions.form[$formInternals].formInitializationId,
        formValidationErrorsMap: validationState.formValidationErrorsMap,
        formValidationMeta: validationState.formValidationMeta,
        fieldsValidation: validationState.fieldsValidation,
    });
}
